<template>
  <div class="staff-picker">
    <el-input
      ref="searchInput"
      v-model="query"
      placeholder="Search staff by name"
      clearable
      :prefix-icon="Search"
      @keydown.enter.prevent="chooseFirstMatch"
    />

    <ul class="staff-picker__list">
      <template v-if="!query.trim()">
        <li v-if="unassignable">
          <button type="button" class="staff-picker__option" @click="choose(null)">
            <UserRoundX :size="16" class="staff-picker__icon" /> Unassign
          </button>
        </li>
        <li v-if="currentStaffId && assignableToMe">
          <button type="button" class="staff-picker__option" @click="choose(currentStaffId)">
            <UserRoundCheck :size="16" class="staff-picker__icon" /> Assign to me
          </button>
        </li>
      </template>

      <template v-for="section in sections" :key="section.label">
        <li class="staff-picker__section">{{ section.label }}</li>
        <li v-for="person in section.staff" :key="person.id">
          <button type="button" class="staff-picker__option" @click="choose(person.id)">
            <StaffInitialsAvatar :full-name="person.fullName" small />
            {{ person.fullName }}
          </button>
        </li>
      </template>

      <li v-if="loading" class="staff-picker__hint"><i class="fas fa-spinner fa-spin tw-mr-1" /> Loading staff…</li>
      <li v-else-if="!matches.length" class="staff-picker__hint">
        {{ query.trim() ? `No staff match “${query.trim()}”` : "No staff found" }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { Search } from "@element-plus/icons-vue"
import { UserRoundCheck, UserRoundX } from "lucide-vue-next"
import StaffInitialsAvatar from "@/modules/patient-conversations/components/StaffInitialsAvatar.vue"
import { PatientConversationStaffOption } from "@/modules/patient-conversations/composables/staff-directory"
import { STAFF_PICKER_SECTIONS } from "@/modules/patient-conversations/constants/patient-conversation-labels"
import { useCurrentStaffId } from "@/modules/patient-conversations/composables/current-staff"

// The searchable staff list shared by the inbox "Assign" control, the New message assignee and the thread's Reassign
const props = withDefaults(
  defineProps<{
    staff: ReadonlyArray<PatientConversationStaffOption>
    loading?: boolean
    /** Offers "Unassign" as the first option. */
    unassignable?: boolean
    /** Offers "Assign to me"; off when everything selected is already the viewer's. */
    assignableToMe?: boolean
  }>(),
  { loading: false, unassignable: false, assignableToMe: true }
)

const emit = defineEmits<{
  /** The chosen assignee's `usr_…` id, or `null` for "Unassign". */
  (e: "select", staffId: string | null): void
}>()

const currentStaffId = useCurrentStaffId()
const query = ref<string>("")
const searchInput = ref<{ focus: () => void }>()

const matches = computed<ReadonlyArray<PatientConversationStaffOption>>(() => {
  const needle = query.value.trim().toLowerCase()
  return needle ? props.staff.filter((person) => person.fullName.toLowerCase().includes(needle)) : props.staff
})

const sections = computed(() =>
  STAFF_PICKER_SECTIONS.map((section) => ({
    label: section.label,
    staff: matches.value.filter((person) => person.role === section.role)
  })).filter((section) => section.staff.length > 0)
)

function choose(staffId: string | null): void {
  emit("select", staffId)
}

function chooseFirstMatch(): void {
  const first = sections.value[0]?.staff[0]
  if (first) choose(first.id)
}

function focus(): void {
  searchInput.value?.focus()
}

function reset(): void {
  query.value = ""
}

defineExpose({ focus, reset })
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.staff-picker
  display: flex
  flex-direction: column
  gap: 6px

  &__list
    list-style: none
    margin: 0
    padding: 0
    max-height: 320px
    overflow-y: auto

  &__option
    display: flex
    align-items: center
    gap: 8px
    width: 100%
    padding: 7px 10px
    border: 0
    border-radius: 6px
    background: transparent
    color: $empty-text
    font-size: 14px
    text-align: left
    cursor: pointer

    &:hover, &:focus-visible
      background-color: $vitable-green-background

  &__icon
    flex: none
    color: $grey

  &__section
    padding: 10px 10px 4px
    font-size: 11px
    font-weight: 700
    letter-spacing: 0.06em
    color: $grey

  &__hint
    padding: 8px 10px
    font-size: 12px
    color: $grey
</style>
